import { find, keys, map, get } from 'lodash'
import moment from 'moment'

import competitorIcon from '../styles/assets/addons/competitor.svg'
import trackedkeywordsIcon from '../styles/assets/addons/tracked_keywords.svg'
import userIcon from '../styles/assets/addons/user.svg'
import dailysearchIcon from '../styles/assets/addons/daily_search.svg'
import domainIcon from '../styles/assets/addons/domain.svg'
import metricsIcon from '../styles/assets/addons/metrics.svg'
import pencilIcon from '../styles/assets/dashboard/pencil.svg'
import { TIME_ZONE } from '../components/panels/AppBanner/consts'
import { palette, setOpacity } from '../styles/variables'

import { formatNumber as defaultFormatNumber } from './numbers'
import { getCountryInfo } from './locations'
import currencies from './currencies.json'
import { translate } from './translation'
import { getLoggedUser } from './userAuth'
import { allAddonsTypes } from './addonTypes'
import { isWithinDateRange } from './isWithinDateRange'
import { safeJsonParse } from './safe-json-parse'

export const RECURLY_PUBLIC_KEY = process.env.REACT_APP_RECURLY_PUBLIC_KEY || ''

export const getUnsupportedCurrencies = () => {
  const unsupportedCurrencies = [
    'AED',
    'ARS',
    'BDT',
    'CLP',
    'CNY',
    'COP',
    'EGP',
    'IDR',
    'ILS',
    'KRW',
    'MAD',
    'MYR',
    'NGN',
    'PEN',
    'PHP',
    'PKR',
    'RON',
    'RUB',
    'THB',
    'TRY',
    'TWD',
    'ZAR',
    'BRL',
    'MXN',
    'PHP',
    'BDT',
    'ARS',
    'PKR',
    'CLP',
    'EGP',
    'PEN',
    'MAD',
    'NGN',
    'CNY',
  ]
  if (!sessionStorage.getItem('gbInrCur')) {
    unsupportedCurrencies.push('INR')
  }
  return unsupportedCurrencies
}

export const RecurlyErrorCodes = [
  'generic_decline',
  'insufficient_funds',
  'do_not_honor',
  '10417',
  'transaction_not_allowed',
  'fraudulent',
  '10201',
  'processing_error',
  'pickup_card',
  'incorrect_cvc',
  '10207',
  'expired_card',
  'lost_card',
  'reenter_transaction',
  'stolen_card',
  'card_decline_rate_limit_exceeded',
  '10204',
  '500',
  '10412',
  'issuer_not_available',
]

export const getUserSubscription = () =>
  JSON.parse(localStorage.getItem('subscription'))

export const setUserSubscription = (subscription) =>
  localStorage.setItem('subscription', JSON.stringify(subscription))

export const getRegionFromCountryCode = (countryCode = 'US') => {
  let currencyData = currencies[countryCode]
  const country = getCountryInfo(countryCode)
  let region = { lang: country.languageCode, locId: country.locationId }
  if (!currencyData) {
    currencyData = currencies.US
    region = { lang: 'en', locId: 2840 }
  }

  return region
}

export const getRegionFromCurrency = (currency) => {
  const country = map(currencies, (value, key) => ({
    countryCode: key,
    currency: value.currency,
  })).find((v) => v.currency === currency.toUpperCase()) || {
    countryCode: 'US',
  }
  return getRegionFromCountryCode(country.countryCode)
}

export const getCurrencyAndRegion = (countryCode) => {
  let currency = currencies[countryCode] && currencies[countryCode].currency
  let planCode
  const country = getCountryInfo(countryCode)
  let region = { lang: country.languageCode, locId: country.locationId }
  if (!currency || currency !== getSupportedCurrency(currency)) {
    currency = getSupportedCurrency(currency)
    region = { lang: 'en', locId: 2840 }
    planCode = 'other'
  } else {
    planCode = currency.toLowerCase()
  }
  return { currency, region, planCode }
}

export const getPlanPrefix = (planCode) => {
  if (planCode === 'other') return 'global'
  return 'local'
}

const formatNumber = (
  x,
  withCurrency = false,
  decimalCounts = 2,
  forcedRegion = null,
  useAbbreviation = true,
  abbrUnit = 1000000,
) => {
  if (x % 1 !== 0) {
    return defaultFormatNumber(
      x,
      withCurrency,
      decimalCounts,
      forcedRegion,
      useAbbreviation,
      abbrUnit,
    )
  }
  return defaultFormatNumber(
    x,
    withCurrency,
    0,
    forcedRegion,
    useAbbreviation,
    abbrUnit,
  )
}

export const getActiveCoupon = (currentCoupon) => {
  const activeCoupon = {}

  if (currentCoupon) {
    const {
      exp = null,
      discount = {},
      applies_to_all_plans: appliesToAllPlans = false,
      plans: couponPlans = [],
    } = currentCoupon

    const momentFromTimestamp = exp
      ? !moment.unix(exp).isSameOrBefore(moment())
      : true
    if (momentFromTimestamp) {
      activeCoupon.appliesToAllPlans = appliesToAllPlans
      activeCoupon.couponPlans = couponPlans
      activeCoupon.discount = discount
    }
  }

  return activeCoupon
}

export const getDiscountCoef = (planTier, activeCoupon, currency) => {
  if (!planTier || !Object.keys(planTier)?.length) {
    return {}
  }
  const shouldCouponApply =
    (activeCoupon?.appliesToAllPlans ||
      activeCoupon?.couponPlans?.includes(planTier.plan_code)) &&
    activeCoupon?.discount?.percent
  const coef = shouldCouponApply ? 1 - activeCoupon.discount.percent / 100 : 1
  const monthQty = planTier?.plan_code?.includes('_year') ? 12 : 1
  const value = Math.ceil(planTier.currencies[currency] * coef)
  return {
    value,
    monthlyValue: value / monthQty,
    currency: planTier.currencies[currency],
    coef,
    percent: activeCoupon?.discount?.percent,
    activeCoupon: !!shouldCouponApply,
  }
}

export const getTierPrices = (
  countryCode,
  withCurrencySymbol = true,
  addonPrice = 0,
  coupon = undefined,
) => {
  const user = getLoggedUser()
  const activeCoupon = getActiveCoupon(coupon || user?.coupon || {})

  const { currency, region, planCode } = getCurrencyAndRegion(countryCode)
  const plans = getPlans()

  if (!plans) {
    return {
      free: '',
      tier0: '',
      tier1: '',
      tier2: '',
      tier3: '',
      restart: '',
    }
  }

  const lifetimePlanTier0 = plans.tier0.lifetime[getPlanPrefix(planCode)] || {}

  const monthlyPlanTier1 = plans.tier1.monthly[getPlanPrefix(planCode)] || {}
  const yearlyPlanTier1 = plans.tier1.yearly[getPlanPrefix(planCode)] || {}
  const lifetimePlanTier1 = plans.tier1.lifetime[getPlanPrefix(planCode)] || {}

  const monthlyPlanTier2 = plans.tier2.monthly[getPlanPrefix(planCode)] || {}
  const yearlyPlanTier2 = plans.tier2.yearly[getPlanPrefix(planCode)] || {}
  const lifetimePlanTier2 = plans.tier2.lifetime[getPlanPrefix(planCode)] || {}

  const monthlyPlanTier3 = plans.tier3.monthly[getPlanPrefix(planCode)] || {}
  const yearlyPlanTier3 = plans.tier3.yearly[getPlanPrefix(planCode)] || {}
  const lifetimePlanTier3 = plans.tier3.lifetime[getPlanPrefix(planCode)] || {}

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const promoItems = JSON.parse(localStorage.getItem('promo_items') || '{}')

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bfYearlyPlanTier1 =
  //   promoItems?.tier1?.yearly[getPlanPrefix(planCode)] || {}
  // const bfYearlyPlanTier2 =
  //   promoItems?.tier2?.yearly[getPlanPrefix(planCode)] || {}
  // const bfYearlyPlanTier3 =
  //   promoItems?.tier3?.yearly[getPlanPrefix(planCode)] || {}

  const monthlyPlanRestart = plans.restart.monthly.local || {}

  const lifetimePlanTier0Coupon = getDiscountCoef(
    lifetimePlanTier0,
    activeCoupon,
    currency,
  )

  const monthlyPlanTier1Coupon = getDiscountCoef(
    monthlyPlanTier1,
    activeCoupon,
    currency,
  )
  const yearlyPlanTier1Coupon = getDiscountCoef(
    yearlyPlanTier1,
    activeCoupon,
    currency,
  )

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bfPlanTier1Coupon = getDiscountCoef(
  //   bfYearlyPlanTier1,
  //   activeCoupon,
  //   currency,
  // )

  const lifetimePlanTier1Coupon = getDiscountCoef(
    lifetimePlanTier1,
    activeCoupon,
    currency,
  )

  const monthlyPlanTier2Coupon = getDiscountCoef(
    monthlyPlanTier2,
    activeCoupon,
    currency,
  )
  const yearlyPlanTier2Coupon = getDiscountCoef(
    yearlyPlanTier2,
    activeCoupon,
    currency,
  )

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bfPlanTier2Coupon = getDiscountCoef(
  //   bfYearlyPlanTier2,
  //   activeCoupon,
  //   currency,
  // )

  const lifetimePlanTier2Coupon = getDiscountCoef(
    lifetimePlanTier2,
    activeCoupon,
    currency,
  )

  const monthlyPlanTier3Coupon = getDiscountCoef(
    monthlyPlanTier3,
    activeCoupon,
    currency,
  )
  const yearlyPlanTier3Coupon = getDiscountCoef(
    yearlyPlanTier3,
    activeCoupon,
    currency,
  )

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bfPlanTier3Coupon = getDiscountCoef(
  //   bfYearlyPlanTier3,
  //   activeCoupon,
  //   currency,
  // )

  const lifetimePlanTier3Coupon = getDiscountCoef(
    lifetimePlanTier3,
    activeCoupon,
    currency,
  )

  const monthlyPlanRestartCoupon = getDiscountCoef(
    monthlyPlanRestart,
    activeCoupon,
    currency,
  )

  return {
    free: {
      monthly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(0, true, 2, region, false),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate('price_a_month', formatNumber(0, true, 2, region, false)),
      yearly: withCurrencySymbol
        ? translate(
            'per_year',
            translate(
              'currency_at_symbol',
              formatNumber(0, true, 2, region, false),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate('price_a_year', formatNumber(0, true, 2, region, false)),
    },
    tier0: {
      planCode: {
        lifetime: {
          applied: lifetimePlanTier0Coupon?.activeCoupon,
          code: lifetimePlanTier0.plan_code,
        },
      },
      monthly: 0,
      yearly: 0,
      lifetime: withCurrencySymbol
        ? translate(
            'per_lifetime',
            translate(
              'currency_at_symbol',
              formatNumber(
                lifetimePlanTier0Coupon.value,
                true,
                0,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_lifetime',
            formatNumber(lifetimePlanTier0Coupon.value, true, 2, region, false),
          ),
    },
    tier1: {
      planCode: {
        lifetime: {
          applied: lifetimePlanTier1Coupon?.activeCoupon,
          code: lifetimePlanTier1.plan_code,
        },
        monthly: {
          applied: monthlyPlanTier1Coupon?.activeCoupon,
          code: monthlyPlanTier1.plan_code,
        },
        yearly: {
          applied: yearlyPlanTier1Coupon?.activeCoupon,
          code: yearlyPlanTier1.plan_code,
        },
        // BF_ATP_BUNDLE: uncomment when we have Bundle
        // bf_offer: {
        //   applied:
        //     (bfPlanTier1Coupon?.activeCoupon || 0) +
        //     (yearlyPlanTier1Coupon?.activeCoupon || 0),
        //   code: bfYearlyPlanTier1.code,
        // },
      },
      monthlyPrice: {
        price: monthlyPlanTier1Coupon.value + addonPrice,
        value: formatNumber(
          monthlyPlanTier1Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      monthly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                monthlyPlanTier1Coupon.value + addonPrice,
                true,
                0,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              monthlyPlanTier1Coupon.value + addonPrice,
              true,
              2,
              region,
              false,
            ),
          ),
      yearlyPrice: {
        price: yearlyPlanTier1Coupon.value + addonPrice,
        value: formatNumber(
          yearlyPlanTier1Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      yearlyPerYear: withCurrencySymbol
        ? formatNumber(yearlyPlanTier1Coupon.value, true, 2, region, false)
        : formatNumber(yearlyPlanTier1Coupon.value, true, 2, region, false),
      yearlyOriginPerMonth: formatNumber(
        monthlyPlanTier1Coupon.value,
        true,
        2,
        region,
        false,
      ),
      yearly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                yearlyPlanTier1Coupon.monthlyValue,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              yearlyPlanTier1Coupon.monthlyValue,
              true,
              2,
              region,
              false,
            ),
          ),
      // BF_ATP_BUNDLE: uncomment when we have Bundle
      // bf_offer: withCurrencySymbol
      //   ? translate(
      //       'per_month',
      //       translate(
      //         'currency_at_symbol',
      //         formatNumber(
      //           (bfPlanTier1Coupon?.activeCoupon || 0) +
      //             (yearlyPlanTier1Coupon?.activeCoupon || 0),
      //           true,
      //           2,
      //           region,
      //           false,
      //         ),
      //         '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
      //       ),
      //     )
      //   : translate(
      //       'price_a_month',
      //       formatNumber(
      //         (bfPlanTier1Coupon?.activeCoupon || 0) +
      //           (yearlyPlanTier1Coupon?.activeCoupon || 0),
      //         true,
      //         2,
      //         region,
      //         false,
      //       ),
      //     ),
      lifetimePrice: {
        price: lifetimePlanTier1Coupon.value + addonPrice,
        value: formatNumber(
          lifetimePlanTier1Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      lifetime: withCurrencySymbol
        ? translate(
            'per_lifetime',
            translate(
              'currency_at_symbol',
              formatNumber(
                lifetimePlanTier1Coupon.value,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_lifetime',
            formatNumber(lifetimePlanTier1Coupon.value, true, 2, region, false),
          ),
    },
    tier2: {
      planCode: {
        lifetime: {
          applied: lifetimePlanTier2Coupon?.activeCoupon,
          code: lifetimePlanTier2.plan_code,
        },
        monthly: {
          applied: monthlyPlanTier2Coupon?.activeCoupon,
          code: monthlyPlanTier2.plan_code,
        },
        yearly: {
          applied: yearlyPlanTier2Coupon?.activeCoupon,
          code: yearlyPlanTier2.plan_code,
        },
        // BF_ATP_BUNDLE: uncomment when we have Bundle
        // bf_offer: {
        //   applied:
        //     (bfPlanTier2Coupon?.activeCoupon || 0) +
        //     (yearlyPlanTier2Coupon?.activeCoupon || 0),
        //   code: bfYearlyPlanTier2.code,
        // },
      },
      monthlyPrice: {
        price: monthlyPlanTier2Coupon.value + addonPrice,
        value: formatNumber(
          monthlyPlanTier2Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      monthly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                monthlyPlanTier2Coupon.value + addonPrice,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              monthlyPlanTier2Coupon.value + addonPrice,
              true,
              2,
              region,
              false,
            ),
          ),
      yearlyPrice: {
        price: yearlyPlanTier2Coupon.value + addonPrice,
        value: formatNumber(
          yearlyPlanTier2Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      yearlyPerYear: withCurrencySymbol
        ? formatNumber(yearlyPlanTier2Coupon.value, true, 2, region, false)
        : formatNumber(yearlyPlanTier2Coupon.value, true, 2, region, false),
      yearlyOriginPerMonth: formatNumber(
        monthlyPlanTier2Coupon.value,
        true,
        2,
        region,
        false,
      ),
      yearly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                yearlyPlanTier2Coupon.monthlyValue,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              yearlyPlanTier2Coupon.monthlyValue,
              true,
              2,
              region,
              false,
            ),
          ),
      // BF_ATP_BUNDLE: uncomment when we have Bundle
      // bf_offer: withCurrencySymbol
      //   ? translate(
      //       'per_month',
      //       translate(
      //         'currency_at_symbol',
      //         formatNumber(
      //           (bfPlanTier2Coupon?.activeCoupon || 0) +
      //             (yearlyPlanTier2Coupon?.activeCoupon || 0),
      //           true,
      //           2,
      //           region,
      //           false,
      //         ),
      //         '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
      //       ),
      //     )
      //   : translate(
      //       'price_a_month',
      //       formatNumber(
      //         (bfPlanTier2Coupon?.activeCoupon || 0) +
      //           (yearlyPlanTier2Coupon?.activeCoupon || 0),
      //         true,
      //         2,
      //         region,
      //         false,
      //       ),
      //     ),
      lifetimePrice: {
        price: lifetimePlanTier2Coupon.value + addonPrice,
        value: formatNumber(
          lifetimePlanTier2Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      lifetime: withCurrencySymbol
        ? translate(
            'per_lifetime',
            translate(
              'currency_at_symbol',
              formatNumber(
                lifetimePlanTier2Coupon.value,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_lifetime',
            formatNumber(lifetimePlanTier2Coupon.value, true, 2, region, false),
          ),
    },
    tier3: {
      planCode: {
        lifetime: {
          applied: lifetimePlanTier3Coupon?.activeCoupon,
          code: lifetimePlanTier3.plan_code,
        },
        monthly: {
          applied: monthlyPlanTier3Coupon?.activeCoupon,
          code: monthlyPlanTier3.plan_code,
        },
        yearly: {
          applied: monthlyPlanTier3Coupon?.activeCoupon,
          code: monthlyPlanTier3.plan_code,
        },
        // BF_ATP_BUNDLE: uncomment when we have Bundle
        // bf_offer: {
        //   applied:
        //     (bfPlanTier3Coupon?.activeCoupon || 0) +
        //     (yearlyPlanTier3Coupon?.activeCoupon || 0),
        //   code: bfYearlyPlanTier3.code,
        // },
      },
      monthlyPrice: {
        price: monthlyPlanTier3Coupon.value + addonPrice,
        value: formatNumber(
          monthlyPlanTier3Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      monthly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                monthlyPlanTier3Coupon.value + addonPrice,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              monthlyPlanTier3Coupon.value + addonPrice,
              true,
              2,
              region,
              false,
            ),
          ),
      yearlyPrice: {
        price: yearlyPlanTier3Coupon.value + addonPrice,
        value: formatNumber(
          yearlyPlanTier3Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      yearlyPerYear: withCurrencySymbol
        ? formatNumber(yearlyPlanTier3Coupon.value, true, 2, region, false)
        : formatNumber(yearlyPlanTier3Coupon.value, true, 2, region, false),
      yearlyOriginPerMonth: formatNumber(
        monthlyPlanTier3Coupon.value,
        true,
        2,
        region,
        false,
      ),
      yearly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                yearlyPlanTier3Coupon.monthlyValue,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              yearlyPlanTier3Coupon.monthlyValue,
              true,
              2,
              region,
              false,
            ),
          ),
      // BF_ATP_BUNDLE: uncomment when we have Bundle
      // bf_offer: withCurrencySymbol
      //   ? translate(
      //       'per_month',
      //       translate(
      //         'currency_at_symbol',
      //         formatNumber(
      //           (bfPlanTier3Coupon?.activeCoupon || 0) +
      //             (yearlyPlanTier3Coupon?.activeCoupon || 0),
      //           true,
      //           2,
      //           region,
      //           false,
      //         ),
      //         '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
      //       ),
      //     )
      //   : translate(
      //       'price_a_month',
      //       formatNumber(
      //         (bfPlanTier3Coupon?.activeCoupon || 0) +
      //           (yearlyPlanTier3Coupon?.activeCoupon || 0),
      //         true,
      //         2,
      //         region,
      //         false,
      //       ),
      //     ),
      lifetimePrice: {
        price: lifetimePlanTier3Coupon.value + addonPrice,
        value: formatNumber(
          lifetimePlanTier3Coupon.value + addonPrice,
          true,
          0,
          region,
          false,
        ),
        currency,
      },
      lifetime: withCurrencySymbol
        ? translate(
            'per_lifetime',
            translate(
              'currency_at_symbol',
              formatNumber(
                lifetimePlanTier3Coupon.value,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_lifetime',
            formatNumber(lifetimePlanTier3Coupon.value, true, 2, region, false),
          ),
    },
    restart: {
      planCode: {
        monthly: {
          applied: monthlyPlanRestartCoupon?.activeCoupon,
          code: monthlyPlanRestart.plan_code,
        },
      },
      monthly: withCurrencySymbol
        ? translate(
            'per_month',
            translate(
              'currency_at_symbol',
              formatNumber(
                monthlyPlanRestartCoupon.value + addonPrice,
                true,
                2,
                region,
                false,
              ),
              '', // As we are determined to hide currency code, put empty string instead of `currency` variable.
            ),
          )
        : translate(
            'price_a_month',
            formatNumber(
              monthlyPlanRestartCoupon.value + addonPrice,
              true,
              2,
              region,
              false,
            ),
          ),
    },
  }
}

export const getSubscriptionPlanId = (
  countryCode = 'US',
  tier = 'tier1',
  planInterval = 'monthly',
) => {
  const { planCode } = getCurrencyAndRegion(countryCode)
  const plans = getPlans()

  if (!plans) return null

  const planId =
    plans[tier][planInterval][
      tier === 'restart' ? 'local' : getPlanPrefix(planCode)
    ].plan_code
  return planId
}

export const getSubscriptionPlanPrice = (
  countryCode = 'US',
  tier = 'tier1',
  planInterval = 'monthly',
) => {
  const { currency, planCode } = getCurrencyAndRegion(countryCode)
  const plans = getPlans()

  if (!plans) return 0

  const planPrices =
    plans[tier][tier === 'tier0' ? 'lifetime' : planInterval][
      tier === 'restart' ? 'local' : getPlanPrefix(planCode)
    ].currencies || {}
  return planPrices[currency] || 0
}

export const getLifetimePrice = (countryCode, tier) => {
  const { currency, region, planCode } = getCurrencyAndRegion(countryCode)
  const plans = getPlans()
  if (plans && tier && tier !== 'restart') {
    const lifetimePlan = plans[tier].lifetime[getPlanPrefix(planCode)] || {}

    return formatNumber(
      lifetimePlan.currencies[currency],
      true,
      0,
      region,
      false,
    )
  }
  return 0
}

export const getSubscriptionOptions = ({
  countryCode = 'US',
  tier = 'tier1',
  _isLifetimeValid = false,
  coupon = null,
  addonPrice = 0,
  item = {},
  promoFeature = {},
}) => {
  if (tier === 'free') return null
  const plans = getPlans()

  if (!plans) return []

  const couponInfo = coupon || getLoggedUser()?.coupon || {}
  const { currency, region, planCode } = getCurrencyAndRegion(countryCode)

  const monthlyPlan =
    tier === 'tier0'
      ? {}
      : plans[tier].monthly[
          tier === 'restart' ? 'local' : getPlanPrefix(planCode)
        ] || {}
  const monthlyPlanId = monthlyPlan.plan_code
  const yearlyPlan =
    tier === 'tier0' || tier === 'restart'
      ? {}
      : plans[tier].yearly[getPlanPrefix(planCode)] || {}
  const yearlyPlanId = yearlyPlan.plan_code
  const lifetimePlan =
    tier === 'restart'
      ? {}
      : plans[tier].lifetime[getPlanPrefix(planCode)] || {}
  const lifetimePlanId = lifetimePlan.plan_code

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const promoItems = JSON.parse(localStorage.getItem('promo_items') || 'null')
  // const atpPrices = ATP_MONTHLY_PRICES

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bfYearlyPlan =
  //   tier === 'tier0' || tier === 'restart'
  //     ? {}
  //     : promoItems?.[tier]?.yearly?.[getPlanPrefix(planCode)] || {}

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const atpMontlyPlan =
  //   tier === 'tier0' || tier === 'restart'
  //     ? {}
  //     : atpPrices?.[tier]?.monthly?.[getPlanPrefix(planCode)] || {}

  const itemPrice = item
    ? get(item, [getPlanPrefix(planCode), 'currencies', currency], 0)
    : 0
  const itemCode = item
    ? get(item, [getPlanPrefix(planCode), 'code'], null)
    : null

  const activeCoupon = getActiveCoupon(couponInfo)

  const monthlyPlanCoupon = getDiscountCoef(monthlyPlan, activeCoupon, currency)
  const yearlyPlanCoupon = getDiscountCoef(yearlyPlan, activeCoupon, currency)
  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bfPlanCoupon = getDiscountCoef(bfYearlyPlan, activeCoupon, currency)
  // const atpMontlyCoupon = getDiscountCoef(atpMontlyPlan, activeCoupon, currency)
  const lifetimePlanPlanCoupon = getDiscountCoef(
    lifetimePlan,
    activeCoupon,
    currency,
  )

  if (tier === 'tier0') {
    return [
      {
        id: 1,
        label: 'lifetime_membership',
        planLabel: 'lifetime',
        plan: 'lifetime',
        value: lifetimePlanId,
        unitPrice: formatNumber(
          lifetimePlan.currencies[currency] + itemPrice,
          true,
          2,
          region,
          false,
        ),
        monthlyPrice: 0,
        yearlyPrice: 0,
        description: translate('onetime_fee'),
        subscriptionDescription: translate(
          'free_then_lifetime',
          formatNumber(
            lifetimePlan.currencies[currency] + itemPrice,
            true,
            2,
            region,
            false,
          ),
        ),
        subscriptionPeriod: translate('onetime_fee'),
        currency,
        tier,
        region,
        itemCode,
      },
    ]
  }

  if (!yearlyPlanId || tier === 'restart') {
    return [
      {
        id: 1,
        label: 'billed_monthly',
        planLabel: 'monthly',
        plan: 'monthly',
        value: monthlyPlanId,
        unitPrice: formatNumber(
          monthlyPlan.currencies[currency],
          true,
          2,
          region,
          false,
        ),
        monthlyPrice: formatNumber(
          monthlyPlan.currencies[currency],
          true,
          2,
          region,
          false,
        ),
        yearlyPrice: formatNumber(
          12 * monthlyPlan.currencies[currency],
          true,
          2,
          region,
          false,
        ),
        description: translate('full_price'),
        subscriptionDescription: translate(
          'ubersuggest_pro_is',
          formatNumber(
            monthlyPlan.currencies[currency],
            true,
            2,
            region,
            false,
          ),
          formatNumber(
            12 * monthlyPlan.currencies[currency],
            true,
            2,
            region,
            false,
          ),
        ),
        subscriptionPeriod: translate('nper_month'),
        currency,
        tier,
        region,
        discountPrice: monthlyPlanCoupon?.activeCoupon
          ? formatNumber(monthlyPlanCoupon.value, true, 2, region, false)
          : null,
        discountPercent: monthlyPlanCoupon?.activeCoupon
          ? `${monthlyPlanCoupon.percent}%`
          : '',
        discountPercentNumber: monthlyPlanCoupon?.activeCoupon
          ? monthlyPlanCoupon?.percent
          : 0,
      },
    ]
  }

  const planYearly = {
    id: 2,
    label: 'yearly',
    planLabel: 'yearly',
    plan: 'yearly',
    value: yearlyPlanId,
    unitPrice: formatNumber(
      yearlyPlanCoupon?.value || yearlyPlan.currencies[currency],
      true,
      2,
      region,
      false,
    ),
    monthlyPrice: formatNumber(
      (yearlyPlanCoupon?.value || yearlyPlan.currencies[currency]) / 12,
      true,
      2,
      region,
      false,
    ),
    yearlyPrice: formatNumber(
      yearlyPlanCoupon?.value || yearlyPlan.currencies[currency],
      true,
      2,
      region,
      false,
    ),
    description: translate(
      'save_',
      translate(
        'currency_at_symbol',
        formatNumber(
          12 * monthlyPlan.currencies[currency] -
            (yearlyPlanCoupon?.value || yearlyPlan.currencies[currency]),
          true,
          2,
          region,
        ),
        '',
      ),
    ),
    subscriptionDescription: `${translate('bf_billing_message')}. ${translate(
      'free_trial',
      7,
    )}. ${translate('cancel_anytime')}.`,
    updateSubscriptionDescription: `${translate('bf_billing_message')}.`,
    subscriptionPeriod: translate('yearly').toLowerCase(),
    currency,
    tier,
    region,
    previousePrice: formatNumber(
      12 * monthlyPlan.currencies[currency],
      true,
      2,
      region,
      false,
    ),
    discountPrice: yearlyPlanCoupon?.activeCoupon
      ? formatNumber(yearlyPlanCoupon.value, true, 2, region, false)
      : null,
    discountPercent: yearlyPlanCoupon?.activeCoupon
      ? `${yearlyPlanCoupon.percent}%`
      : '',
    discountPercentNumber: yearlyPlanCoupon?.activeCoupon
      ? yearlyPlanCoupon?.percent
      : 0,
    // BF_ATP_BUNDLE: Uncomment after BF promotion
    // benefit: translate('save_x_percent', 80),
    // limitedOffer: translate('black_friday'),
    theme: 'dark',
    hideLimitedBadge: true,
  }

  // BF_ATP_BUNDLE: uncomment when we have Bundle
  // const bf_offer = {
  //   id: 0,
  //   label: 'bf_offer',
  //   checkoutLabel: 'bf_offer',
  //   planLabel: PROMO_LABEL_VALUE,
  //   plan: 'yearly',
  //   value: yearlyPlanId,
  //   theme: 'dark',
  //   previousePrice: formatNumber(
  //     12 * (atpMontlyCoupon?.value || bfYearlyPlan.currencies[currency]) +
  //       12 * monthlyPlan.currencies[currency],
  //     true,
  //     2,
  //     region,
  //     false,
  //   ),
  //   unitPrice: formatNumber(
  //     (bfPlanCoupon?.value || bfYearlyPlan?.currencies?.[currency] || 0) +
  //       (yearlyPlanCoupon?.value || yearlyPlan.currencies[currency]),
  //     true,
  //     2,
  //     region,
  //     false,
  //   ),
  //   monthlyPrice: formatNumber(
  //     ((bfPlanCoupon?.value || bfYearlyPlan?.currencies?.[currency] || 0) +
  //       (yearlyPlanCoupon?.value || yearlyPlan?.currencies?.[currency] || 0)) /
  //       12,
  //     true,
  //     2,
  //     region,
  //     false,
  //   ),
  //   yearlyPrice: formatNumber(
  //     (bfPlanCoupon?.value || bfYearlyPlan?.currencies?.[currency] || 0) +
  //       (yearlyPlanCoupon?.value || yearlyPlan.currencies[currency]),
  //     true,
  //     2,
  //     region,
  //     false,
  //   ),
  //   subscriptionDescription: translate('bf_checkout_message'),
  //   subscriptionPeriod: translate('yearly').toLowerCase(),
  //   currency,
  //   tier,
  //   region,
  //   itemCode: bfYearlyPlan.plan_code,
  //   limitedOffer: translate('black_friday'),
  //   hideLimitedBadge: true,
  // }

  const planList = [
    {
      id: 1,
      label: 'billed_monthly',
      planLabel: 'monthly',
      plan: 'monthly',
      value: monthlyPlanId,
      unitPrice: formatNumber(
        monthlyPlanCoupon?.value || monthlyPlan.currencies[currency],
        true,
        2,
        region,
        false,
      ),
      monthlyPrice: formatNumber(
        (monthlyPlanCoupon?.value || monthlyPlan.currencies[currency]) +
          addonPrice,
        true,
        2,
        region,
        false,
      ),
      yearlyPrice: formatNumber(
        12 * (monthlyPlanCoupon?.value || monthlyPlan.currencies[currency]),
        true,
        2,
        region,
        false,
      ),
      description: translate('full_price'),
      subscriptionDescription: translate(
        'bf_monthly_trial_card',
        formatNumber(
          12 * (monthlyPlanCoupon?.value || monthlyPlan.currencies[currency]),
          true,
          2,
          region,
          false,
        ),
      ),
      updateSubscriptionDescription: `${translate(
        'monthly_access_form_heading',
      )}. ${translate('no_hidden_fees')}`,
      subscriptionPeriod: translate('nper_month'),
      currency,
      tier,
      region,
      discountPrice: monthlyPlanCoupon?.activeCoupon
        ? formatNumber(monthlyPlanCoupon.value, true, 2, region, false)
        : null,
      discountPercent: monthlyPlanCoupon?.activeCoupon
        ? `${monthlyPlanCoupon.percent}%`
        : '',
      discountPercentNumber: monthlyPlanCoupon?.activeCoupon
        ? monthlyPlanCoupon?.percent
        : 0,
      benefit: translate('save_x_percent', 70),
    },
    planYearly,
    // BF_ATP_BUNDLE: uncomment when we have Bundle
    // bf_offer,
    {
      id: 3,
      label: 'lifetime_membership',
      planLabel: 'lifetime',
      plan: 'lifetime',
      value: lifetimePlanId,
      unitPrice: formatNumber(
        lifetimePlanPlanCoupon?.value ||
          lifetimePlan.currencies[currency] + itemPrice,
        true,
        2,
        region,
        false,
      ),
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: translate('onetime_fee'),
      subscriptionDescription: translate(
        'free_then_lifetime',
        formatNumber(
          lifetimePlanPlanCoupon?.value ||
            lifetimePlan.currencies[currency] + itemPrice,
          true,
          2,
          region,
          false,
        ),
      ),
      subscriptionPeriod: translate('onetime_fee'),
      currency,
      tier,
      region,
      discountPrice: lifetimePlanPlanCoupon?.activeCoupon
        ? formatNumber(lifetimePlanPlanCoupon.value, true, 2, region, false)
        : null,
      discountPercent: lifetimePlanPlanCoupon?.activeCoupon
        ? `${lifetimePlanPlanCoupon.percent}%`
        : '',
      discountPercentNumber: lifetimePlanPlanCoupon?.activeCoupon
        ? lifetimePlanPlanCoupon?.percent
        : 0,
      itemCode,
      benefit: translate('save_x_percent', 90),
    },
  ]

  if (
    promoFeature?.theme?.promotionLabel &&
    promoFeature?.value?.valid_plans?.length &&
    isWithinDateRange({
      startDate: promoFeature.value.start_date,
      endDate: promoFeature.value.end_date,
      timeZone: TIME_ZONE,
    })
  ) {
    const {
      promotionLabel: { text },
    } = promoFeature.theme
    return planList.reduce((acc, plan) => {
      const promoOffer = promoFeature.value.valid_plans.find(
        ({ plan_interval: planInterval }) => {
          return plan.plan === planInterval
        },
      )
      if (promoOffer) {
        if (promoOffer?.promo_offer && text) {
          plan.hideLimitedBadge = true
          plan.limitedOffer = text
        }
        acc.push(plan)
      }
      return acc
    }, [])

    // BF_ATP_BUNDLE: uncomment when we have Bundle
    // return planList.filter(({ plan }) => {
    //   return promoItem['valid_plans'].some(
    //     ({ plan_interval: planInterval }) => plan === planInterval,
    //   )
    // })
  }

  return planList.filter(({ plan }) => plan !== 'yearly')
}

export const setPlans = (plans) =>
  localStorage.setItem('plans', JSON.stringify(plans))

export const getPlans = () => {
  const plans = safeJsonParse(localStorage.getItem('plans'))
  return plans
}

export const getSupportedCurrency = (currency) => {
  if (!currency) return 'USD'
  if (getUnsupportedCurrencies().some((c) => c === currency)) return 'USD'
  return currency
}

export const getAvailableAddons = () =>
  JSON.parse(localStorage.getItem('addons'))

export const setAvailableAddons = (addons) =>
  localStorage.setItem('addons', JSON.stringify(addons))

export const AIW_ADDON_DEFAULT = 'aiw_default'

export const AIW_ADDONS = [
  allAddonsTypes.ADDON_AIW_BLOGGER,
  allAddonsTypes.ADDON_AIW_BUSINESS,
  allAddonsTypes.ADDON_AIW_SHAKESPEARE,
]

export const addonMainLimitNames = {
  [allAddonsTypes.ADDON_COMPETITOR]: 'competitors_per_project',
  [allAddonsTypes.ADDON_TRACKEDKEYWORDS]: 'keywords_per_project',
  [allAddonsTypes.ADDON_USER]: 'users_per_account',
  [allAddonsTypes.ADDON_DAILYSEARCH]: 'reports',
  [allAddonsTypes.ADDON_DOMAIN]: 'projects',
  [allAddonsTypes.ADDON_KEYWORD_METRICS_UPDATES]:
    'monthly_keyword_metrics_updates',
  [allAddonsTypes.ADDON_AIW_BLOGGER]: 'aiw_words',
  [allAddonsTypes.ADDON_AIW_BUSINESS]: 'aiw_words',
  [allAddonsTypes.ADDON_AIW_SHAKESPEARE]: 'aiw_words',
}

export const getAddonTypeFromCode = (code) => {
  let addonType
  if (code) {
    for (const key of Object.keys(allAddonsTypes)) {
      if (code.includes(allAddonsTypes[key])) {
        addonType = allAddonsTypes[key]
        break
      }
    }
  }
  return addonType
}

export const getAddonByCode = (addons = {}, code = undefined) => {
  if (!addons[code]) return
  return { code, ...addons[code] }
}

export const getAddonByKey = (addons = {}, key = '') => {
  const addonCode = find(keys(addons), (code) => code.includes(key))
  return getAddonByCode(addons, addonCode)
}

export const getAddonLimit = (addonDetail = {}) => {
  const addonType =
    addonDetail?.addonType || getAddonTypeFromCode(addonDetail.code)
  return addonDetail?.limits[addonMainLimitNames[addonType]] || 0
}

export const AddonCardIcons = {
  [allAddonsTypes.ADDON_COMPETITOR]: {
    icon: competitorIcon,
    bgColor: setOpacity(palette.orange30, 0.25),
    text: 'additional_competitors',
  },
  [allAddonsTypes.ADDON_TRACKEDKEYWORDS]: {
    icon: trackedkeywordsIcon,
    bgColor: setOpacity(palette.green, 0.25),
    text: 'additional_tracked_kws',
  },
  [allAddonsTypes.ADDON_USER]: {
    icon: userIcon,
    bgColor: setOpacity(palette.green, 0.25),
    text: 'additional_user',
  },
  [allAddonsTypes.ADDON_DAILYSEARCH]: {
    icon: dailysearchIcon,
    bgColor: setOpacity(palette.info, 0.25),
    text: 'additional_searches',
  },
  [allAddonsTypes.ADDON_DOMAIN]: {
    icon: domainIcon,
    bgColor: setOpacity(palette.orange30, 0.25),
    text: 'additional_project',
  },
  [allAddonsTypes.ADDON_KEYWORD_METRICS_UPDATES]: {
    icon: metricsIcon,
    bgColor: setOpacity(palette.orange30, 0.25),
    text: 'kw_metrics_credits',
  },
  [allAddonsTypes.ADDON_AIW_BLOGGER]: {
    icon: pencilIcon,
    bgColor: setOpacity(palette.orange30, 0.25),
    text: 'number_of_words',
  },
  [allAddonsTypes.ADDON_AIW_BUSINESS]: {
    icon: pencilIcon,
    bgColor: setOpacity(palette.orange30, 0.25),
    text: 'number_of_words',
  },
  [allAddonsTypes.ADDON_AIW_SHAKESPEARE]: {
    icon: pencilIcon,
    bgColor: setOpacity(palette.orange30, 0.25),
    text: 'number_of_words',
  },
}

export const isAIWAddon = (addonType) =>
  AIW_ADDONS.some((type) => type === addonType)

export const getAddonPrice = (addon, currency, planCode) => {
  if (addon?.prices) {
    if (addon.prices && planCode !== 'other' && addon.prices[currency])
      return addon.prices[currency]
    return addon.prices.USD
  }
  return 0
}
